<template>
  <LeadInput
    v-if="definition.type === 'text'"
    :modelValue="(modelValue as string) ?? ''"
    :placeholder="placeholder"
    :disabled="disabled"
    :errors="errors"
    autofocus
    @update:model-value="(value: string) => emit('update:modelValue', value === '' ? null : value)"
    @keyup.enter="emit('submit')"
    @keyup.esc="emit('cancel')" />

  <LeadInput
    v-else-if="definition.type === 'number'"
    type="number"
    inputmode="decimal"
    :modelValue="modelValue ?? ''"
    :placeholder="placeholder"
    :disabled="disabled"
    :errors="errors"
    autofocus
    @beforeinput="rejectNonNumericInput"
    @update:model-value="onNumberInput"
    @keyup.enter="emit('submit')"
    @keyup.esc="emit('cancel')" />

  <LeadDatePicker
    v-else-if="definition.type === 'date'"
    :model-value="(modelValue as string) ?? null"
    :placeholder="placeholder || $t('candidate.fields.date_placeholder')"
    :disabled="disabled"
    :errors="errors"
    @update:model-value="(value: string | null) => emit('modelValueAndSubmit', value)" />

  <LeadSelect
    v-else
    :model-value="(modelValue as string) ?? undefined"
    :options="selectOptions"
    :placeholder="placeholder || $t('candidate.fields.select_placeholder')"
    :disabled="disabled"
    :errors="errors"
    @update:model-value="(value) => emit('modelValueAndSubmit', String(value))" />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LeadInput from '@/components/form/LeadInput.vue';
import LeadSelect from '@/components/form/LeadSelect.vue';
import LeadDatePicker from '@/components/form/LeadDatePicker.vue';
import type { CandidateFieldDefinition } from '@/types/candidate';

interface Props {
  definition: CandidateFieldDefinition;
  modelValue: string | number | null;
  placeholder?: string;
  disabled?: boolean;
  errors?: string | string[] | null;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: undefined,
  disabled: false,
  errors: null,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null): void;
  // date/select commit immediately on change — they're already discrete-choice widgets, so an
  // extra confirm step is friction; text/number wait for an explicit submit (Enter) instead
  (e: 'modelValueAndSubmit', value: string | number | null): void;
  (e: 'submit'): void;
  (e: 'cancel'): void;
}>();

const selectOptions = computed(() => props.definition.options?.choices ?? []);

const onNumberInput = (value: string | number) => {
  if (value === '' || value === null || value === undefined) {
    emit('update:modelValue', null);
    return;
  }
  const parsed = Number(value);
  emit('update:modelValue', Number.isNaN(parsed) ? null : parsed);
};

// lithium's FormInput already strips non-numeric characters, but only after they briefly appear —
// typing "12abc34" silently becomes "1234" once the emit round-trips back down, which reads as the
// field eating the user's input. Rejecting the disallowed keystroke before it's ever inserted (via
// beforeinput, so paste/drag/IME are covered too, not just typed keys) means invalid characters
// never appear in the first place, instead of visibly appearing then vanishing.
const rejectNonNumericInput = (event: Event) => {
  const inputEvent = event as InputEvent;
  // insertions only — inputEvent.data is null for deletions, cut, undo, etc., which are always fine
  if (inputEvent.data !== null && /[^0-9.-]/.test(inputEvent.data)) {
    event.preventDefault();
  }
};
</script>
